import {
  type TooltipChildNodeProps,
  pickTooltipChildNodeProps,
} from '@ipfabric/ui-components/Tooltip/Tooltip';
import classNames from 'classnames';
import type { FC, ReactNode, SyntheticEvent } from 'react';
import './Toggle.css';

export type ToggleProps = TooltipChildNodeProps & {
  className?: string;
  disabled?: boolean;
  id?: string;
  value: boolean;
  onChange?: (value: boolean) => void;
  hasError?: boolean;
  hideStateMessage?: boolean;
  /**
   * State label is positioned correctly automatically
   * use this prop only on explicit request by UX
   */
  stateLabelPosition?: 'left' | 'right';
  messages?: {
    on: string;
    off: string;
  };
  label?: ReactNode;
  labelPosition?: 'top' | 'left' | 'right';
  /**
   * For labelPosition 'left' or 'right', this stretches the space between label and
   * control to fill full available width. It should not be used with labelPosition='top'.
   */
  stretch?: boolean;
};

const toggleMsg = {
  on: 'On',
  off: 'Off',
};

export const Toggle: FC<ToggleProps> = ({
  labelPosition = 'top',
  ...props
}) => {
  const fallbackedMessages = props.messages ? props.messages : toggleMsg;

  const stateLabelPosition = props.stateLabelPosition
    ? props.stateLabelPosition
    : {
        top: 'right',
        left: 'right',
        right: 'left',
      }[labelPosition];

  const handleClick = (e: SyntheticEvent) => {
    if (props.onChange) {
      e.preventDefault();
      e.stopPropagation();
      if (!props.disabled) {
        props.onChange(!props.value);
      }
    }
  };

  const renderStateLabel = () => (
    <div
      className={classNames(
        'toggle__state-label',
        props.disabled && 'toggle__state-label--diabled',
        stateLabelPosition === 'left' ? 'pr-2' : 'pl-2',
      )}
    >
      {props.value ? fallbackedMessages.on : fallbackedMessages.off}
    </div>
  );

  const renderLabel = () =>
    props.label && (
      <div
        className={classNames(
          'toggle__label',
          `toggle__label--${labelPosition}`,
        )}
      >
        {props.label}
      </div>
    );

  return (
    <div
      className={classNames(
        'toggle',
        props.disabled && 'toggle--disabled',
        labelPosition !== 'top' && 'flex items-center',
        props.stretch && 'w-full justify-between',
        props.className,
      )}
      id={props.id}
      onClick={handleClick}
      {...pickTooltipChildNodeProps(props)}
    >
      {(labelPosition === 'left' || labelPosition === 'top') && renderLabel()}
      <div className='flex items-center'>
        {!props.hideStateMessage &&
          stateLabelPosition === 'left' &&
          renderStateLabel()}
        <div
          className={classNames(
            'toggle__control',
            props.value ? 'toggle__control--on' : 'toggle__control--off',
            props.hasError && 'toggle__control--error',
            props.disabled && 'toggle__control--disabled',
          )}
        >
          <div className='toggle__circle' />
        </div>
        {!props.hideStateMessage &&
          stateLabelPosition === 'right' &&
          renderStateLabel()}
      </div>
      {labelPosition === 'right' && renderLabel()}
    </div>
  );
};
